import { doc, getDoc } from 'firebase/firestore';
import { db } from './config';

const SETTINGS_DOC = 'settings/website';

// Default fallback settings
const DEFAULT_SETTINGS = {
  businessName: 'KO Holistic Health and Wellbeing',
  businessTagline: 'Enhancing lives through holistic healing and spiritual guidance',
  ownerName: 'Kirstie Warburton',
  ownerTitle: 'Holistic Health Practitioner',
  email: 'info@koholistichealth.com',
  phone: '0439 386 551',
  phoneFormatted: '+61439386551',
  city: 'Sydney',
  state: 'NSW',
  country: 'Australia',
  availability: 'Monday - Saturday',
  availabilityDetail: 'Flexible hours available',
  instagram: 'https://www.instagram.com/koholistichealth',
  facebook: 'https://www.facebook.com/koholistichealth',
  credentials: [
    'Reiki Master Teacher',
    'Restorative Yin Instructor',
    'Meditation Teacher',
    'Soul Coaching Practitioner'
  ],
  footerTagline: 'Enhancing lives through holistic healing and spiritual guidance',
  bookingEnabled: true,
  bookingLink: '/booking',
  bookingText: 'Book Now',
  showBanner: false
};

// Cache settings in memory
let cachedSettings = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const settingsService = {
  /**
   * Get all website settings with caching
   */
  async getSettings() {
    try {
      // Return cached settings if still valid
      if (cachedSettings && cacheTimestamp && Date.now() - cacheTimestamp < CACHE_DURATION) {
        return cachedSettings;
      }

      const docRef = doc(db, ...SETTINGS_DOC.split('/'));
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        cachedSettings = DEFAULT_SETTINGS;
        cacheTimestamp = Date.now();
        return DEFAULT_SETTINGS;
      }

      const settings = {
        ...DEFAULT_SETTINGS,
        ...docSnap.data()
      };

      cachedSettings = settings;
      cacheTimestamp = Date.now();

      return settings;
    } catch (error) {
      console.error('Error fetching settings:', error);
      return DEFAULT_SETTINGS;
    }
  },

  /**
   * Get contact information
   */
  async getContactInfo() {
    try {
      const settings = await this.getSettings();
      return {
        email: settings.email,
        phone: settings.phone,
        phoneFormatted: settings.phoneFormatted,
        city: settings.city,
        state: settings.state,
        country: settings.country,
        fullAddress: settings.fullAddress,
        availability: settings.availability,
        availabilityDetail: settings.availabilityDetail
      };
    } catch (error) {
      console.error('Error fetching contact info:', error);
      return DEFAULT_SETTINGS;
    }
  },

  /**
   * Get social media links
   */
  async getSocialMedia() {
    try {
      const settings = await this.getSettings();
      return {
        instagram: settings.instagram,
        facebook: settings.facebook,
        linkedin: settings.linkedin,
        youtube: settings.youtube
      };
    } catch (error) {
      console.error('Error fetching social media:', error);
      return {
        instagram: DEFAULT_SETTINGS.instagram,
        facebook: DEFAULT_SETTINGS.facebook
      };
    }
  },

  /**
   * Clear cache (useful after updates)
   */
  clearCache() {
    cachedSettings = null;
    cacheTimestamp = null;
  }
};

export default settingsService;
